export const TAX_FILING_SINGLE = 'single';
export const TAX_FILING_JOINT = 'joint';
export const TAX_FILING_SEPARATE = 'separate';
export const TAX_FILING_HEAD_OF_HOUSEHOLD = 'head_of_household';

export type TaxFilingStatus = typeof TAX_FILING_JOINT | typeof TAX_FILING_SINGLE | typeof TAX_FILING_HEAD_OF_HOUSEHOLD | typeof TAX_FILING_SEPARATE;

type BracketSet = number[];
type RateSet = number[];

const ORDINARY_INCOME_TAX_BRACKET_SINGLE_THRESHOLD: BracketSet = [
    0,
    11925,
    48475,
    103350,
    197300,
    250525,
    626350,
];

const ORDINARY_INCOME_TAX_BRACKET_MARRIED_SEPARATE_THRESHOLD: BracketSet = [
    0,
    11925,
    48475,
    103350,
    197300,
    250525,
    375800,
];

const ORDINARY_INCOME_TAX_BRACKET_JOINT_THRESHOLD: BracketSet = [
    0,
    23850,
    96950,
    206700,
    394600,
    501050,
    751600,
];

const ORDINARY_INCOME_TAX_BRACKET_HEAD_HOUSEHOLD_THRESHOLD: BracketSet = [
    0,
    17000,
    64850,
    103350,
    197300,
    250500,
    626350,
];

const LT_CAP_GAINS_TAX_BRACKET_SINGLE_THRESHOLD: BracketSet = [
    0,
    48350,
    533400,
];

const LT_CAP_GAINS_TAX_BRACKET_MARRIED_SEPARATE_THRESHOLD: BracketSet = [
    0,
    48350,
    300000
];

const LT_CAP_GAINS_TAX_BRACKET_JOINT_THRESHOLD: BracketSet = [
    0,
    96700,
    600050,
];

const LT_CAP_GAINS_TAX_BRACKET_HEAD_HOUSEHOLD_THRESHOLD: BracketSet = [
    0,
    63000,
    566700,
];

const ORDINARY_INCOME_BRACKET_SETS: {[status: string]: BracketSet} = {
    [TAX_FILING_SINGLE]: ORDINARY_INCOME_TAX_BRACKET_SINGLE_THRESHOLD,
    [TAX_FILING_JOINT]: ORDINARY_INCOME_TAX_BRACKET_JOINT_THRESHOLD,
    [TAX_FILING_SEPARATE]: ORDINARY_INCOME_TAX_BRACKET_MARRIED_SEPARATE_THRESHOLD,
    [TAX_FILING_HEAD_OF_HOUSEHOLD]: ORDINARY_INCOME_TAX_BRACKET_HEAD_HOUSEHOLD_THRESHOLD,
}

const LT_CAPITAL_GAINS_BRACKET_SETS: {[status: string]: BracketSet} = {
    [TAX_FILING_SINGLE]: LT_CAP_GAINS_TAX_BRACKET_SINGLE_THRESHOLD,
    [TAX_FILING_JOINT]: LT_CAP_GAINS_TAX_BRACKET_JOINT_THRESHOLD,
    [TAX_FILING_SEPARATE]: LT_CAP_GAINS_TAX_BRACKET_MARRIED_SEPARATE_THRESHOLD,
    [TAX_FILING_HEAD_OF_HOUSEHOLD]: LT_CAP_GAINS_TAX_BRACKET_HEAD_HOUSEHOLD_THRESHOLD,
}

const STANDARD_DEDUCTION_SINGLE = 15000;
const STANDARD_DEDUCTION_JOINT = 30000;
const STANDARD_DEDUCTION_MARRIED_SEPARATE = 15000;
const STANDARD_DEDUCTION_HEAD_HOUSEHOLD = 22500;

const STANDARD_DEDUCTION = {
    [TAX_FILING_SINGLE]: STANDARD_DEDUCTION_SINGLE,
    [TAX_FILING_JOINT]: STANDARD_DEDUCTION_JOINT,
    [TAX_FILING_SEPARATE]: STANDARD_DEDUCTION_MARRIED_SEPARATE,
    [TAX_FILING_HEAD_OF_HOUSEHOLD]: STANDARD_DEDUCTION_HEAD_HOUSEHOLD,
}

export const ORDINARY_INCOME_TAX_RATE = [
    .10,
    .12,
    .22,
    .24,
    .32,
    .35,
    .37,
];

export const LT_CAP_GAINS_TAX_RATE = [
    0,
    .15,
    .20,
];

export const MAX_REAL_ESTATE_DEPRECIATION_RECAPTURE = .25;

// Calculate depreciation recapture tax using ordinary income rates up to 25% cap
const calculateDepreciationRecaptureTax = (amount: number, totalTaxableIncome: number, bracketSet: BracketSet): number => {
    // First calculate tax using ordinary income rates
    const ordinaryTax = calculateTaxFromBracketSet(amount, totalTaxableIncome, bracketSet, ORDINARY_INCOME_TAX_RATE);
    
    // Then calculate what it would be at the 25% flat rate
    const cappedTax = amount * MAX_REAL_ESTATE_DEPRECIATION_RECAPTURE;
    
    // Return the lower of the two
    return Math.min(ordinaryTax, cappedTax);
};

const findBracketIndex = (amount: number, bracketSet: BracketSet): number => {
    let i: number;

    for (i = 0; i < bracketSet.length; i++) {
        if (amount <= bracketSet[i]){
            break;
        }
    }

    return i === 0 ? 0 : i - 1;
};

interface TaxableGains {
    depreciationRecapture: number;
    capitalGain: number;
    suspendedPassiveLoss: number;
}

export class Taxes {
    constructor(
        public ordinaryIncome: number,
        public ordinaryPassiveIncome: number,
        public ltCapitalGains: number,
        public depreciationRecapture: number,
    ) {}

    getTotalTaxes(): number {
        return this.ordinaryIncome + this.ordinaryPassiveIncome + this.ltCapitalGains + this.depreciationRecapture;
    }

    getTotalSyndicationTaxes(): number {
        return this.ordinaryPassiveIncome + this.ltCapitalGains + this.depreciationRecapture;
    }
}

export const calculateTaxWithStandardDeduction = (
    ordinaryIncome: number,
    taxableGains: TaxableGains,
    currentYearPassiveIncome: number,
    filingStatus: TaxFilingStatus,
    round = true,
): Taxes => {
    const deduction = Math.min(ordinaryIncome, STANDARD_DEDUCTION[filingStatus]) * -1;
    const tax = calculateTax(ordinaryIncome, deduction, taxableGains, currentYearPassiveIncome, filingStatus);

    if (round) {
        return new Taxes(
            Math.round(tax.ordinaryIncome),
            Math.round(tax.ordinaryPassiveIncome),
            Math.round(tax.ltCapitalGains),
            Math.round(tax.depreciationRecapture),
        );
    }

    return tax;
}

// TODO: This doesn't include tax credits like EITC...might be needed or not because we're talking about higher income earners here
// this is assuming a non-real-estate-professional -- passive losses cannot offset active income
const calculateTax = (
    ordinaryIncome: number,
    ordinaryIncomeAdjustment: number,
    taxableGains: TaxableGains,
    currentYearPassiveIncome: number, // AFTER expenses like depreciation have been applied
    filingStatus: TaxFilingStatus,
): Taxes => {
    let suspendedPassiveLossRemaining = taxableGains.suspendedPassiveLoss;

    // Calculate total taxable income including passive income and depreciation
    const totalActiveIncome = ordinaryIncome + ordinaryIncomeAdjustment;
    let passiveIncome = currentYearPassiveIncome;
    const totalTaxableOrdinaryIncome = totalActiveIncome + currentYearPassiveIncome;
    if (totalTaxableOrdinaryIncome < 0) {
        // this really shouldn't ever happen so we're going to throw an error
        throw new Error('Total taxable ordinary income is negative -- this shouldn\'t be possible');
    }
    const ordinaryIncomeBracketSet = ORDINARY_INCOME_BRACKET_SETS[filingStatus];
    
    let ordinaryIncomeTax = 0;
    let ordinaryPassiveIncomeTax = 0;
    // If total taxable income is 0, no taxes are owed
    if (totalTaxableOrdinaryIncome > 0) {     
        // calculate ordinary income first
        ordinaryIncomeTax = calculateTaxFromBracketSet(totalActiveIncome, 0, ordinaryIncomeBracketSet, ORDINARY_INCOME_TAX_RATE);

        if (suspendedPassiveLossRemaining > 0) {
            const passiveIncomeAdjustment = Math.min(suspendedPassiveLossRemaining, passiveIncome);
            passiveIncome -= passiveIncomeAdjustment;
            suspendedPassiveLossRemaining -= passiveIncomeAdjustment;
        }
        ordinaryPassiveIncomeTax = calculateTaxFromBracketSet(passiveIncome, totalActiveIncome, ordinaryIncomeBracketSet, ORDINARY_INCOME_TAX_RATE);
    }

    // calculate depreciation recapture - apply 25% to the full amount
    if (suspendedPassiveLossRemaining > 0) {
        const depreciationRecaptureAdjustment = Math.min(suspendedPassiveLossRemaining, taxableGains.depreciationRecapture);
        taxableGains.depreciationRecapture -= depreciationRecaptureAdjustment;
        suspendedPassiveLossRemaining -= depreciationRecaptureAdjustment;
    }
    const depreciationRecaptureTax = calculateDepreciationRecaptureTax(taxableGains.depreciationRecapture, totalTaxableOrdinaryIncome, ordinaryIncomeBracketSet);

    // calculate capital gains
    const capitalGainsBracketSet = LT_CAPITAL_GAINS_BRACKET_SETS[filingStatus];
    if (suspendedPassiveLossRemaining > 0) {
        const capitalGainAdjustment = Math.min(suspendedPassiveLossRemaining, taxableGains.capitalGain);
        taxableGains.capitalGain -= capitalGainAdjustment;
        suspendedPassiveLossRemaining -= capitalGainAdjustment;
    }
    const capitalGainsTax = calculateTaxFromBracketSet(taxableGains.capitalGain, totalTaxableOrdinaryIncome + taxableGains.depreciationRecapture, capitalGainsBracketSet, LT_CAP_GAINS_TAX_RATE);


    return new Taxes(
        ordinaryIncomeTax,
        ordinaryPassiveIncomeTax,
        capitalGainsTax,
        depreciationRecaptureTax,
    );
}

const calculateTaxFromBracketSet = (amount: number, startingPoint: number, bracketSet: BracketSet, rateSet: RateSet): number => {
    if (bracketSet.length !== rateSet.length) {
        throw new Error(`Cannot calculate tax for brackets ${JSON.stringify(bracketSet)} and rates ${JSON.stringify(rateSet)}`);
    }

    let tax = 0;
    let remaingAmount = amount;

    // find starting bracket idx
    const startingIdx = findBracketIndex(startingPoint, bracketSet);
    

    // iterate through brackets
    for(let i = startingIdx; i < bracketSet.length; i++) {
        const current = bracketSet[i];
        const next = bracketSet[i+1];

        if (!remaingAmount) {
            break;
        }

        let amountToCalculate: number;
        if (next){
            const bracketDelta = i === startingIdx ? next - startingPoint : next - current;
            amountToCalculate = Math.min(bracketDelta, remaingAmount);
        } else {
            amountToCalculate = remaingAmount;
        }

        const calculatedTax = amountToCalculate * rateSet[i];

        tax += calculatedTax;
        remaingAmount -= amountToCalculate;
    }

    return tax;
};

export const calculateTaxableGainsFromSyndication = (
    years: number,
    totalDepreciation: number,
    equityPosition: number,
    proceeds: number,
    carriedLoss: number,
): TaxableGains => {
    // Calculate adjusted basis by subtracting total depreciation from original investment
    const adjustedBasis = equityPosition - totalDepreciation;
    
    // Calculate total gain as proceeds minus adjusted basis
    const totalGain = proceeds - adjustedBasis;
    
    if (totalGain <= 0) {
        return {
            depreciationRecapture: 0,
            capitalGain: 0,
            suspendedPassiveLoss: carriedLoss,
        };
    }

    // The portion of gain up to total depreciation is recapture
    const recaptureAmount = Math.min(totalGain, totalDepreciation);
    
    // Any gain beyond recapture is capital gain
    const capitalGainAmount = Math.max(0, totalGain - recaptureAmount);

    return {
        depreciationRecapture: recaptureAmount,
        capitalGain: capitalGainAmount,
        suspendedPassiveLoss: carriedLoss,
    }
};


